/**
 * The edge sheet. Same discipline as the Modal, different geometry: it comes
 * from the right, it is full height, and it is sized for a record rather than
 * for a question.
 *
 * A drawer rather than a modal when the thing being shown is a detail of what
 * is on the page behind it, because the edge attachment says "this belongs to
 * that" in a way a centred box does not. It is still modal in behaviour. A
 * non-modal drawer was considered and rejected: with the table behind it still
 * live, a person can change the row the drawer is describing and the drawer
 * silently goes on describing the old one.
 *
 * `side` exists for exactly one caller. The shell's navigation on a tablet is
 * this component, and a navigation drawer has to come from the edge the
 * sidebar lives on or the gesture is wrong.
 */

import { useId, useRef } from 'react'
import type { ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { CloseIcon } from '@/components/icons/Icons'
import { Button } from '@/components/primitives'
import {
  scrimPointerDown,
  useBackgroundInert,
  useEscapeToClose,
  useFocusTrap,
  useOverlayPortal,
} from '../overlay'
import styles from './Drawer.module.css'

export interface DrawerProps {
  open: boolean
  onClose(): void
  /**
   * The accessible name, always. Separate from `title` because the shell's
   * navigation drawer needs a name without a visible heading above the
   * wordmark it already contains.
   *
   * Constant, like every accessible name here. It does not change when the
   * drawer opens or when its content changes.
   */
  label: string
  /** Visible heading. Omit for a drawer whose content carries its own. */
  title?: ReactNode
  description?: string
  children: ReactNode
  footer?: ReactNode
  side?: 'left' | 'right'
  size?: 'sm' | 'md' | 'lg'
  dismissOnScrim?: boolean
  initialFocusRef?: RefObject<HTMLElement | null>
  className?: string
}

export function Drawer({
  open,
  onClose,
  label,
  title,
  description,
  children,
  footer,
  side = 'right',
  size = 'md',
  dismissOnScrim = true,
  initialFocusRef,
  className,
}: DrawerProps) {
  const host = useOverlayPortal(open)
  const panelRef = useRef<HTMLDivElement>(null)
  const descriptionId = useId()

  useEscapeToClose(open, onClose)
  useBackgroundInert(open)
  useFocusTrap(open, panelRef, initialFocusRef)

  if (!host) return null

  const panelClassName = [styles.panel, styles[size], className].filter(Boolean).join(' ')

  return createPortal(
    <div
      className={styles.scrim}
      data-side={side}
      onPointerDown={dismissOnScrim ? scrimPointerDown(onClose) : undefined}
    >
      <div
        ref={panelRef}
        className={panelClassName}
        data-side={side}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <header className={styles.header} data-bare={title || description ? undefined : ''}>
          {title || description ? (
            <div className={styles.headings}>
              {title ? <h2 className={styles.title}>{title}</h2> : null}
              {description ? (
                <p className={styles.description} id={descriptionId}>
                  {description}
                </p>
              ) : null}
            </div>
          ) : null}
          <Button variant="ghost" size="sm" iconOnly aria-label="Close" onClick={onClose}>
            <CloseIcon size={18} />
          </Button>
        </header>

        <div className={styles.body}>{children}</div>

        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    host,
  )
}
